import { useEffect, useId, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { IconButton } from './Button';

/**
 * Modal dialog.
 *
 * The prototype rendered a scrim div with `role="dialog"` and nothing else:
 * Escape did not close it, focus stayed behind it, and Tab walked the page
 * underneath. This adds the three behaviours a dialog has to have —
 *
 *   Escape closes.
 *   Focus moves inside on open and returns to the trigger on close.
 *   Tab cycles within the dialog.
 *
 * — plus `aria-modal` and a labelled title, and it keeps the click-the-scrim
 * dismissal the original had.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly children: ReactNode;
  readonly footer?: ReactNode | undefined;
  readonly onClose: () => void;
  readonly wide?: boolean | undefined;
}

export function Dialog({
  title,
  subtitle,
  children,
  footer,
  onClose,
  wide = false,
}: DialogProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const restoreRef = useRef<Element | null>(null);

  useEffect(() => {
    restoreRef.current = document.activeElement;
    const panel = panelRef.current;
    // Focus the first control, or the panel itself when the body is read-only.
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    return () => {
      if (restoreRef.current instanceof HTMLElement) restoreRef.current.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );

      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`dlg ${wide ? 'dlg--lg' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="dlg-hd">
          <div>
            <h4 id={titleId}>{title}</h4>
            {subtitle !== undefined && <span className="caption">{subtitle}</span>}
          </div>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </div>
        <div className="dlg-bd">{children}</div>
        {footer !== undefined && <div className="dlg-ft">{footer}</div>}
      </div>
    </div>
  );
}
